import { z } from 'zod';
import { LocalizedStringSchema, MoneySchema, TimestampFieldsSchema } from './common';

// ─── Layout Styles ─────────────────────────────────────────────────────────
export const MiceLayoutStyleSchema = z.enum([
  'theater',
  'classroom',
  'uShape',
  'banquet',
  'boardroom',
  'cocktail',
  'hollowSquare',
  'cabaret',
]);
export type MiceLayoutStyle = z.infer<typeof MiceLayoutStyleSchema>;

// ─── MICE Package Schema ──────────────────────────────────────────────────
export const MicePackageSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: LocalizedStringSchema,
  description: LocalizedStringSchema.optional(),
  pricePerPax: MoneySchema.optional(),
  fixedPrice: MoneySchema.optional(),
  includes: z.array(z.string()).default([]),
  minPax: z.number().int().optional(),
  active: z.boolean().default(true),
});
export type MicePackage = z.infer<typeof MicePackageSchema>;

// ─── MICE Time Slot Schema ─────────────────────────────────────────────────
export const MiceTimeSlotSchema = z.object({
  id: z.string(), // 'half_day_am' | 'half_day_pm' | 'full_day' | 'evening' | 'hourly'
  name: LocalizedStringSchema,
  startTime: z.string(), // '08:30'
  endTime: z.string(),   // '12:00'
  basePrice: MoneySchema.optional(),
});
export type MiceTimeSlot = z.infer<typeof MiceTimeSlotSchema>;

// ─── MICE Outlet / Function Room Schema ────────────────────────────────────
export const MiceOutletSchema = z
  .object({
    id: z.string(),
    type: z.literal('mice'),
    name: LocalizedStringSchema,
    description: LocalizedStringSchema.optional(),
    enabled: z.boolean().default(true),
    sortOrder: z.number().default(1),

    // Physical Space Specs
    areaSqm: z.number().optional(),
    ceilingHeightMeters: z.number().optional(),
    dimension: z.string().optional(),
    floorLevel: z.string().optional(),

    // Capacities by Layout Style
    capacities: z.object({
      theater: z.number().optional(),
      classroom: z.number().optional(),
      uShape: z.number().optional(),
      banquet: z.number().optional(),
      boardroom: z.number().optional(),
      cocktail: z.number().optional(),
      hollowSquare: z.number().optional(),
      cabaret: z.number().optional(),
    }).default({}),

    // Standard Time Slots & Packages
    timeSlots: z.array(MiceTimeSlotSchema).default([]),
    packages: z.array(MicePackageSchema).default([]),

    // Amenities & Equipment
    amenities: z.array(z.object({
      id: z.string(),
      name: LocalizedStringSchema,
      icon: z.string().optional(),
      isComplimentary: z.boolean().default(true),
    })).default([]),

    // Booking Mode
    bookingMode: z.enum(['instant', 'rfp', 'hybrid']).default('hybrid'),
    depositRequiredPercent: z.number().optional(),

    // Media
    images: z.array(z.object({
      url: z.string(),
      caption: z.string().optional(),
    })).default([]),
  })
  .merge(TimestampFieldsSchema);

export type MiceOutlet = z.infer<typeof MiceOutletSchema>;

// ─── MICE Booking Details ─────────────────────────────────────────────────
export const MiceBookingDetailsSchema = z.object({
  functionRoomId: z.string(),
  functionRoomName: z.union([LocalizedStringSchema, z.string()]).optional(),
  eventType: z.enum(['meeting', 'seminar', 'wedding', 'party', 'exhibition', 'other']).default('meeting'),
  layoutStyle: MiceLayoutStyleSchema.default('theater'),
  eventDate: z.string(), // YYYY-MM-DD
  endDate: z.string().optional(),
  timeSlotId: z.string().optional(),
  timeSlotName: z.string().optional(),
  expectedPax: z.number().int().min(1).default(10),
  packageId: z.string().optional(),
  packageName: z.union([LocalizedStringSchema, z.string()]).optional(),
  cateringOptions: z.array(z.string()).optional(),
  equipmentRequests: z.array(z.string()).optional(),
  companyName: z.string().optional(),
  taxId: z.string().optional(),
  billingAddress: z.string().optional(),
  budgetNotes: z.string().optional(),
  specialRequests: z.string().optional(),
  isRfpInquiry: z.boolean().default(false),
});
export type MiceBookingDetails = z.infer<typeof MiceBookingDetailsSchema>;
